import React, { useRef, useState, useEffect } from "react";
// mui
import { useTheme } from "@mui/styles";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
// utils
import getDSCRIndexData from "components/InterestRateChart/utils/getDSCRIndexData";
// 3rd party
import dayjs from "dayjs";
import LightweightCharts, {
  createChart,
  percentageFormatter,
} from "lightweight-charts";
// plugins
import { VertLine } from "./plugins/vertical-line/vertical-line";

export default function InterestRateChart({
  showOnlyTreasury = false,
  loanCreatedAt = null,
  height = null, // Will be set to theme.spacing(50) if null
}) {
  const theme = useTheme();
  const ref = useRef();
  const [chart, setChart] = useState({});
  const [lineSeries, setLineSeries] = useState({});
  const [DSCRIndexData, setDSCRIndexData] = useState([]);
  const [lowestRate, setLowestRate] = useState(null);
  const [lowestSpread, setLowestSpread] = useState(null);
  const [treasuryRate, setTreasuryRate] = useState(null);
  const [latestDate, setLatestDate] = useState(null);

  useEffect(() => {
    (async () => {
      const DSCRIndexData = await getDSCRIndexData();
      setDSCRIndexData(DSCRIndexData);
    })();

    const chart = createChart(ref.current, {
      leftPriceScale: {
        scaleMargins: {
          top: 0.04,
          bottom: 0.0,
        },
        visible: true,
        alignLabels: true, // Align labels with the price scale
        borderColor: theme.palette.primary.main, // Border color
        layout: {
          textColor: "#333", // Text color
          fontSize: 12, // Font size
          fontFamily: "Arial", // Font family
        },
        crossHairMarkerVisible: true, // Crosshair marker visibility
      },
      rightPriceScale: {
        visible: false,
      },
      timeScale: {
        borderVisible: false,
        borderColor: "#fff000",
        visible: true,
        timeVisible: true,
        minBarSpacing: 0.0,
      },
      grid: {
        horzLines: {
          color: "#eee",
        },
        vertLines: {
          color: "#ffffff",
        },
      },
      crosshair: {
        horzLine: {
          visible: false,
          labelVisible: false,
        },
        vertLine: {
          visible: true,
          style: 0,
          width: 2,
          color: "rgba(32, 38, 46, 0.1)",
          labelVisible: false,
        },
      },
    });

    setChart(chart);
  }, []);

  useEffect(() => {
    if (DSCRIndexData.length > 0) {
      ////////// 5 year treasury
      const treasury = DSCRIndexData.map((item) => {
        return {
          time: item.time,
          value: parseFloat(item["5 Yr US Treasury"]) / 100,
        };
      });

      const treasuryLS = chart.addLineSeries({
        priceLineVisible: true,
        priceLineWidth: 1,
        color: "#165472",
        priceScaleId: "left",
        priceFormat: {
          type: "custom",
          formatter: (price) => {
            return `${(price * 100).toFixed(2)}%`;
          },
        },
        autoscaleInfoProvider: () => {
          if (showOnlyTreasury) {
            // For treasury-only mode, calculate proper range from actual data
            const values = treasury.map((item) => item.value);
            const minValue = Math.min(...values);
            const maxValue = Math.max(...values);
            const padding = (maxValue - minValue) * 0.1; // 10% padding

            return {
              priceRange: {
                minValue: Math.max(0, minValue - padding),
                maxValue: maxValue + padding,
              },
              margins: {
                above: 10,
                below: 10,
              },
            };
          } else {
            // For full DSCR mode, let the chart auto-scale
            return null; // This allows auto-scaling
          }
        },
      });

      treasuryLS.setData(treasury);

      // Set treasury rate for all modes
      setTreasuryRate(treasury[treasury.length - 1].value);
      setLatestDate(
        new Date(treasury[treasury.length - 1].time * 1000).toLocaleDateString(
          "en-US",
          {
            month: "long",
            day: "numeric",
            year: "numeric",
          }
        )
      );

      // Clear DSCR-specific state when showing only treasury
      if (showOnlyTreasury) {
        setLowestRate(null);
        setLowestSpread(null);

        // Add loan creation date marker for treasury-only mode
        if (loanCreatedAt) {
          const loanTimestamp = dayjs(loanCreatedAt).unix();

          // Only show marker if loan date is NOT before the chart data begins
          // If loan is newer than latest data, we'll show it at the latest point
          if (loanTimestamp >= treasury[0].time) {
            let closestTimestamp;

            // If loan is newer than latest data, use the latest data point
            if (loanTimestamp > treasury[treasury.length - 1].time) {
              closestTimestamp = treasury[treasury.length - 1].time;
            } else {
              // Find the closest treasury data point to the loan timestamp
              closestTimestamp = treasury[0].time;
              let minDifference = Math.abs(loanTimestamp - treasury[0].time);

              for (let i = 1; i < treasury.length; i++) {
                const difference = Math.abs(loanTimestamp - treasury[i].time);
                if (difference < minDifference) {
                  minDifference = difference;
                  closestTimestamp = treasury[i].time;
                }
              }
            }

            const verticalLinePlugin = new VertLine(
              chart,
              treasuryLS,
              closestTimestamp,
              {
                color: theme.palette.success.main, // Changed to success green
                width: 3, // Thicker line to make sure it's visible
                labelText: "Loan Created",
                labelBackgroundColor: theme.palette.success.main,
                labelTextColor: "white",
                showLabel: true,
              }
            );

            treasuryLS.attachPrimitive(verticalLinePlugin);
          }
        }
      }

      // Only add DSCR lines if not showOnlyTreasury
      if (!showOnlyTreasury) {
        const DSCRLow = DSCRIndexData.map((item) => {
          return { time: item.time, value: parseFloat(item["DSCR Low"]) / 100 };
        });

        setLowestRate(DSCRLow[DSCRLow.length - 1].value);
        setLowestSpread(
          DSCRLow[DSCRLow.length - 1].value -
            treasury[treasury.length - 1].value
        );

        const DSCRLowLS = chart.addLineSeries({
          priceLineVisible: true,
          priceLineWidth: 1,
          color: "#67a1a8",
          priceScaleId: "left",
        });

        DSCRLowLS.setData(DSCRLow);

        ////////// "DSCR High"
        const DSCRHigh = DSCRIndexData.map((item) => {
          return {
            time: item.time,
            value: parseFloat(item["DSCR High"]) / 100,
          };
        });

        const DSCRHighLS = chart.addLineSeries({
          priceLineVisible: true,
          priceLineWidth: 1,
          color: "#9fc4c6",
          priceScaleId: "left",
        });

        DSCRHighLS.setData(DSCRHigh);

        // tooltip creation for DSCR mode
        if (ref.current) {
          // Access the div using divRef.current
          const container = ref.current;

          const toolTipWidth = 110;
          const toolTipHeight = 250;
          const toolTipMargin = 15;

          // Create and style the tooltip html element
          const toolTip = document.createElement("div");
          toolTip.style = `width: 110px; height: 250px; position: absolute; display: none; padding: 2px; box-sizing: border-box; font-size: 12px; text-align: center; z-index: 1000; top: 12px; left: 12px; pointer-events: none; border: 1px solid; border-radius: 2px;font-family: -apple-system, BlinkMacSystemFont, 'Trebuchet MS', Roboto, Ubuntu, sans-serif; -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale;`;
          toolTip.style.background = "white";
          toolTip.style.color = "black";
          toolTip.style.borderColor = theme.palette.primary.main;

          container.appendChild(toolTip);

          chart.subscribeCrosshairMove((param) => {
            if (
              param.point === undefined ||
              !param.time ||
              param.point.x < 0 ||
              param.point.x > container.clientWidth ||
              param.point.y < 0 ||
              param.point.y > container.clientHeight
            ) {
              toolTip.style.display = "none";
            } else {
              // time will be in the same format that we supplied to setData.
              // thus it will be YYYY-MM-DD
              const dateStr = param.time;
              toolTip.style.display = "block";

              const DSCRHighLSData = param.seriesData.get(DSCRHighLS);
              const DSCRHighLSprice =
                DSCRHighLSData.value !== undefined
                  ? DSCRHighLSData.value
                  : DSCRHighLSData.close;

              const DSCRLowLSData = param.seriesData.get(DSCRLowLS);
              const DSCRLowLSprice =
                DSCRLowLSData.value !== undefined
                  ? DSCRLowLSData.value
                  : DSCRLowLSData.close;

              const data = param.seriesData.get(treasuryLS);
              const price = data.value !== undefined ? data.value : data.close;

              const dscrHighColor = "#9fc4c6";
              const dscrLowColor = "#67a1a8";
              const treasuryColor = "#165472";

              toolTip.innerHTML = `
              <div>
                  <div style="font-size: 24px; margin: 2px 0px; color:${dscrHighColor}">
                      ${(100 * DSCRHighLSprice).toFixed(2)}%
                  </div>
                  <div style="font-size: 10px; margin: 2px 0px; color:${
                    theme.palette.warning.main
                  }">
                      Spread: +${(100 * DSCRHighLSprice - 100 * price).toFixed(
                        2
                      )}%
                  </div>
                  <div style="color: ${"white"}; background-color:${dscrHighColor}; border-radius:2px;padding:2px;text-align:center;">
                      DSCR High
                  </div>
                  
                  <div style="font-size: 24px; margin: 2px 0px; color:${dscrLowColor}">
                      ${(100 * DSCRLowLSprice).toFixed(2)}%
                  </div>
                  <div style="font-size: 10px; margin: 2px 0px; color:${
                    theme.palette.warning.main
                  }">
                        Spread: +${(100 * DSCRLowLSprice - 100 * price).toFixed(
                          2
                        )}%
                  </div>
                  <div style="color: ${"white"}; background-color:${dscrLowColor}; border-radius:2px;padding:2px;text-align:center;">
                      DSCR Low
                  </div>
              
                  <div style="font-size: 24px; margin: 2px 0px; color:${treasuryColor}">
                      ${(100 * price).toFixed(2)}%
                  </div>
                  <div style="color: ${"white"}; background-color:${treasuryColor}; border-radius:2px;padding:2px;text-align:center;">
                      5-Year Treasury
                  </div>
              <div style="font-size: 12px; color: ${"black"};margin-top: 4px; padding:2px;text-align:center;">
                  ${dayjs(dateStr * 1000).format("MM-DD-YYYY")}
              </div>
              </div>`;

              const y = param.point.y;
              let left = param.point.x - 6 * toolTipMargin;

              let top = y + toolTipMargin;
              if (top > container.clientHeight - toolTipHeight) {
                top = y - toolTipHeight - toolTipMargin;
              }
              toolTip.style.left = left + "px";
              toolTip.style.top = top + "px";
            }
          });
        }

        // Add loan creation date marker for DSCR mode
        if (loanCreatedAt) {
          const loanTimestamp = dayjs(loanCreatedAt).unix();

          // Only show marker if loan date is NOT before the chart data begins
          // If loan is newer than latest data, we'll show it at the latest point
          if (loanTimestamp >= treasury[0].time) {
            let closestTimestamp;

            // If loan is newer than latest data, use the latest data point
            if (loanTimestamp > treasury[treasury.length - 1].time) {
              closestTimestamp = treasury[treasury.length - 1].time;
            } else {
              // Find the closest treasury data point to the loan timestamp
              closestTimestamp = treasury[0].time;
              let minDifference = Math.abs(loanTimestamp - treasury[0].time);

              for (let i = 1; i < treasury.length; i++) {
                const difference = Math.abs(loanTimestamp - treasury[i].time);
                if (difference < minDifference) {
                  minDifference = difference;
                  closestTimestamp = treasury[i].time;
                }
              }
            }

            const verticalLinePlugin = new VertLine(
              chart,
              treasuryLS,
              closestTimestamp,
              {
                color: theme.palette.success.main,
                width: 2,
                labelText: "Loan Created",
                labelBackgroundColor: theme.palette.success.main,
                labelTextColor: "white",
                showLabel: true,
              }
            );

            treasuryLS.attachPrimitive(verticalLinePlugin);
          }
        }
      }

      // tooltip creation for treasury-only mode
      if (showOnlyTreasury && ref.current) {
        // Access the div using divRef.current
        const container = ref.current;

        const toolTipWidth = 110;
        const toolTipHeight = 100;
        const toolTipMargin = 15;

        // Create and style the tooltip html element
        const toolTip = document.createElement("div");
        toolTip.style = `width: 110px; height: ${toolTipHeight}px; position: absolute; display: none; padding: 2px; box-sizing: border-box; font-size: 12px; text-align: center; z-index: 1000; top: 12px; left: 12px; pointer-events: none; border: 1px solid; border-radius: 2px;font-family: -apple-system, BlinkMacSystemFont, 'Trebuchet MS', Roboto, Ubuntu, sans-serif; -webkit-font-smoothing: antialiased; -moz-osx-font-smoothing: grayscale;`;
        toolTip.style.background = "white";
        toolTip.style.color = "black";
        toolTip.style.borderColor = theme.palette.primary.main;

        container.appendChild(toolTip);

        chart.subscribeCrosshairMove((param) => {
          if (
            param.point === undefined ||
            !param.time ||
            param.point.x < 0 ||
            param.point.x > container.clientWidth ||
            param.point.y < 0 ||
            param.point.y > container.clientHeight
          ) {
            toolTip.style.display = "none";
          } else {
            // time will be in the same format that we supplied to setData.
            // thus it will be YYYY-MM-DD
            const dateStr = param.time;
            toolTip.style.display = "block";

            const data = param.seriesData.get(treasuryLS);
            const price = data.value !== undefined ? data.value : data.close;

            const treasuryColor = "#165472";

            // Treasury-only tooltip
            toolTip.innerHTML = `
            <div>
                <div style="font-size: 24px; margin: 2px 0px; color:${treasuryColor}">
                    ${(100 * price).toFixed(2)}%
                </div>
                <div style="color: ${"white"}; background-color:${treasuryColor}; border-radius:2px;padding:2px;text-align:center;">
                    5-Year Treasury
                </div>
            <div style="font-size: 12px; color: ${"black"};margin-top: 4px; padding:2px;text-align:center;">
                ${dayjs(dateStr * 1000).format("MM-DD-YYYY")}
            </div>
            </div>`;

            const y = param.point.y;
            let left = param.point.x - 6 * toolTipMargin;

            let top = y + toolTipMargin;
            if (top > container.clientHeight - toolTipHeight) {
              top = y - toolTipHeight - toolTipMargin;
            }
            toolTip.style.left = left + "px";
            toolTip.style.top = top + "px";
          }
        });
      }

      // Ensure proper fitting of all content to show the full data range
      chart.timeScale().fitContent();
    }
  }, [DSCRIndexData, showOnlyTreasury, loanCreatedAt]);

  return (
    <div
      className="parent-element"
      style={{
        position: "relative",
        width: "100%",
        height: height || theme.spacing(50), // Default to 400px equivalent
        marginTop: theme.spacing(2),
      }}
    >
      <div
        className="chart-element"
        ref={ref}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          borderRadius: "8px",
          overflow: "hidden",
          boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
        }}
      ></div>
    </div>
  );
}
